<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { MetaPhoneNumber } from '@/types';

const props = defineProps<{
    phone: MetaPhoneNumber | null;
    isSubmitting: boolean;
}>();

const { t } = useI18n();

const emit = defineEmits<{
    close: [];
    confirm: [pin: string];
}>();

const pin = ref('');
const isValid = computed(() => /^\d{6}$/.test(pin.value));

watch(
    () => props.phone,
    () => {
        pin.value = '';
    },
);

const close = () => {
    if (props.isSubmitting) return;
    pin.value = '';
    emit('close');
};

const submit = () => {
    if (!isValid.value || props.isSubmitting) return;
    emit('confirm', pin.value);
};
</script>

<template>
    <TransitionRoot as="template" :show="Boolean(phone)">
        <Dialog as="div" class="relative z-50" @close="close">
            <TransitionChild
                as="template"
                enter="ease-out duration-200"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-150"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-500/75" />
            </TransitionChild>
            <div class="fixed inset-0 z-50 overflow-y-auto">
                <div class="flex min-h-full items-center justify-center p-4">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-200"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-150"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel class="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                            <DialogTitle class="text-lg font-semibold text-gray-800">
                                {{ t('whatsappMeta.activationDialog.title') }}
                            </DialogTitle>
                            <p class="mt-2 text-sm text-gray-600">
                                {{ t('whatsappMeta.activationDialog.descriptionBeforeNumber') }}
                                <strong>{{
                                    phone?.displayPhoneNumber || phone?.phoneNumberId
                                }}</strong
                                >{{ t('whatsappMeta.activationDialog.descriptionAfterNumber') }}
                            </p>
                            <form class="mt-5" @submit.prevent="submit">
                                <UiTextInput
                                    v-model="pin"
                                    :label="t('whatsappMeta.activationDialog.pinLabel')"
                                    name="whatsapp-registration-pin"
                                    type="password"
                                    inputmode="numeric"
                                    maxlength="6"
                                    autocomplete="new-password"
                                    placeholder="000000"
                                />
                                <p v-if="pin && !isValid" class="text-danger mt-2 text-sm">
                                    {{ t('whatsappMeta.activationDialog.invalidPin') }}
                                </p>
                                <div class="mt-6 flex justify-end gap-3">
                                    <UiButton variant="white" text-variant="primary" @click="close">
                                        {{ t('whatsappMeta.activationDialog.cancel') }}
                                    </UiButton>
                                    <UiButton
                                        html-type="submit"
                                        variant="primary"
                                        text-variant="white"
                                        :disabled="!isValid || isSubmitting"
                                    >
                                        {{ t('whatsappMeta.activationDialog.activate') }}
                                    </UiButton>
                                </div>
                            </form>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
